import { HTMLAttributes, useEffect, useRef, useState } from "react";
import { useSwipeable } from "react-swipeable";

import { IFrame } from "../../molecules/IFrame";
import { NUMBER_OF_ITEMS_TO_PREFETCH } from "./constants";
import { MainViewItem, MainViewItemProps } from "./internal/MainViewItem/MainViewItem";
import { ThumbnailScrollbar } from "./internal/ThumbnailScrollbar/ThumbnailScrollbar";
import { MainViewContainer, Root } from "./styles";

export type CarouselItem = MainViewItemProps & {
  thumbnail: {
    src: string;
    alt: string;
  };
};

/**
 * These props will only be used for Iframe components to determine the functionality of the cookie consent modal
 */
type IframeCookieConsent = {
  /**
   * Determines if the consent modal should be shown to users when viewing Iframes in the main view. Unless otherwise specified, all Iframes will be hidden behind the consent modal until consent has been given.
   */
  hasConsent: boolean;
  /**
   * The function to be called when the Iframe consent modal button is clicked.
   */
  onConsent: () => void;
};

type CarouselProps = {
  /**
   * Instead of accepting children, this entity should accept an array of CarouselItems, which should follow a strict type (see MainViewItem).
   */
  items: CarouselItem[];
  /**
   * This prop only affects images. This controls how many items should be prefetched from the current active position of the carousel. E.g. the default is 1 therefore on page load, the 2nd item in the carousel will be prefetched, assuming it is an image.
   */
  numberOfImagesToPrefetch?: number;
  /**
   * Optional cookie consent props used for Iframe components only.
   */
  iframeCookieConsent?: IframeCookieConsent;
} & Omit<HTMLAttributes<HTMLElement>, "children">;

export function ShowcaseCarousel({
  items,
  numberOfImagesToPrefetch = NUMBER_OF_ITEMS_TO_PREFETCH,
  iframeCookieConsent,
  ...rest
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const moveRight = () =>
    setCurrentIndex((prev) => {
      if (currentIndex < items.length - 1) {
        return prev + 1;
      }
      return prev;
    });

  const moveLeft = () =>
    setCurrentIndex((prev) => {
      if (currentIndex > 0) {
        return prev - 1;
      }
      return prev;
    });

  const swipeHandlers = useSwipeable({
    // Swiping left should move the carousel to the right
    onSwipedLeft: moveRight,
    // Swiping right should move the carousel to the left
    onSwipedRight: moveLeft,
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  // Handles the reloading of iframes when they are no longer the active main view item
  const [consent, setConsent] = useState(iframeCookieConsent?.hasConsent ?? false);
  useEffect(() => {
    setConsent(iframeCookieConsent?.hasConsent ?? false);
  }, [iframeCookieConsent]);

  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Do not run this logic if the user hasn't consented yet, as the src hasnt loaded yet
    if (!rootRef.current || !consent) {
      return;
    }

    // Create an Intersection Observer for each iframe
    const observers: IntersectionObserver[] = [];

    // Select all iframes belonging to this carousel instance
    const iframes = rootRef.current.querySelectorAll("iframe");

    iframes.forEach((iframe) => {
      // Store iframe src in a separate var to be used as a trigger for reloading the iFrame
      const source = iframe.src;
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) {
          iframe.src = source;
        }
      });

      observer.observe(iframe);
      observers.push(observer);
    });

    // Clean up observers when the component unmounts
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [consent]);

  const thumbnailItems = items.map((x) => x.thumbnail);

  return (
    <Root
      {...rest}
      aria-label="Showcase Carousel"
      aria-roledescription="carousel"
      data-testid={ShowcaseCarousel.name}
      ref={rootRef}
    >
      <div
        {...swipeHandlers}
        aria-atomic="false"
        aria-label="Swipeable Highlight"
        aria-live="polite"
      >
        {items.map(({ preset }, i) => {
          return (
            <MainViewContainer
              aria-hidden={currentIndex !== i}
              aria-label={`${i + 1} of ${items.length}`}
              aria-roledescription="slide"
              data-testid={MainViewContainer.displayName}
              isActive={currentIndex === i}
              key={`${MainViewContainer.displayName}${i}`}
              role="group"
            >
              {preset.component === "iframe" ? (
                <MainViewItem
                  preset={{
                    component: "iframe",
                    props: {
                      consentModal: (
                        <IFrame.DefaultConsentModal
                          buttonAction={() => iframeCookieConsent?.onConsent()}
                        />
                      ),
                      ...preset.props,
                      consentConfirmed: consent,
                    },
                  }}
                  shouldPrefetch={currentIndex + numberOfImagesToPrefetch === i}
                />
              ) : (
                <MainViewItem
                  preset={preset}
                  shouldPrefetch={currentIndex + numberOfImagesToPrefetch === i}
                />
              )}
            </MainViewContainer>
          );
        })}
      </div>

      <ThumbnailScrollbar
        moveLeft={moveLeft}
        moveRight={moveRight}
        thumbnailItems={thumbnailItems}
        currentIndex={currentIndex}
        updateCurrentIndex={setCurrentIndex}
      />
    </Root>
  );
}
